import {
  CREATE_SIGNED_DOCUMENT,
  DRAFT_SIGNED_DOCUMENT_ID,
  INIT_RECIPIENT_INFO,
  INIT_RECIPIENTS,
  SET_CURRENT_SIGNED_DOCUMENT_ID,
  SignedDocumentActions,
} from "common/reducers/signedDocument.actions";
import { fromJS, List, Map, Record } from "immutable";
import { RootActions, RootState } from "common/reducers";
import { SignedDocumentRecord, signedDocumentFromJSON as signedDocumentFromJSON } from "common/records/SignedDocument";
import { fetch, loaded, receive } from "common/reducers/Utils";
import {
  FETCH_SIGNED_DOCUMENT,
  RECEIVE_SIGNED_DOCUMENT,
  SET_SIGNED_DOCUMENT_LOADED,
} from "common/reducers/signedDocument.actions";
import { currentOrgId } from "common/selectors/org.selectors";
import { recipientFromJSON } from "common/records/Recipient";
import { currentJob } from "common/selectors/job.selectors";
import { getFullName } from "common/records/User";

export const SignedDocumentStateRecord = Record({
  byId: Map<number, SignedDocumentRecord>(),
  currentId: 0,
});

export const initialState = SignedDocumentStateRecord();
export type SignedDocumentState = typeof initialState;

const model = "signedDocuments";

// Ensure state is not undefined
export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case FETCH_SIGNED_DOCUMENT:
      return fetch(state, model, signedDocumentFromJSON({ id: action.payload })) as RootState;

    case RECEIVE_SIGNED_DOCUMENT:
      return receive(state, model, signedDocumentFromJSON({ ...action.payload.signedDocument }) as any) as RootState;

    case SET_SIGNED_DOCUMENT_LOADED:
      return loaded(state, model, action.payload) as RootState;

    case SET_CURRENT_SIGNED_DOCUMENT_ID:
      return state.setIn([model, "currentId"], action.payload);

    case INIT_RECIPIENTS:
      const scribbleSigners: List<any> =
        (action.payload.validations?.getIn(["data", "signers"]) as List<any>) || List();
      const orgSigners = state.getIn(
        ["orgs", "byId", currentOrgId(state), "settings", "config", "signature_settings", "signers"],
        List(),
      );
      const initSigners = orgSigners.setSize(scribbleSigners.size).map(() => recipientFromJSON({}));
      state = state.setIn([model, "byId", action.payload.id, "recipients"], initSigners);

      scribbleSigners.forEach((scribbleSigner, index) => {
        const role_id = scribbleSigner.get("role");
        const orgSigner = orgSigners.find((os: any) => os.get("role") === role_id) || orgSigners.get(index);

        let recipient = recipientFromJSON({ role_id });
        if (orgSigner) {
          recipient = recipient.merge({
            signer: orgSigner.get("signer"),
            required: orgSigner.get("required"),
            included: orgSigner.get("required"),
            title: orgSigner.get("title"),
          });
        }

        state = reducer(state, SignedDocumentActions.initRecipientInfo(recipient, action.payload.id, index));
      });

      return state;

    case INIT_RECIPIENT_INFO:
      const { id, index } = action.payload;
      let { recipient } = action.payload;
      const job = currentJob(state);
      if (!job) {
        console.error("Job not found");
        return state;
      }

      switch (recipient.signer) {
        case "job":
          recipient = recipient.merge({ name: job.customer_name, email: job.email });
          break;
        case "salesrep":
          if (job.assignments && job.assignments.size > 0) {
            const assignee = job.assignments.get(0);
            recipient = recipient.merge({ name: getFullName(assignee?.user), email: assignee?.user?.email });
          } else {
            recipient = recipient.merge({ name: "", email: "" });
          }

          break;
        default:
          recipient = recipient.merge({ name: "", email: "" });
          break;
      }
      return state.setIn([model, "byId", id, "recipients", index], recipient);

    case CREATE_SIGNED_DOCUMENT: {
      const { response, estimateId, metadata } = action.payload;
      const { document, signature_validations } = response;
      const validations = fromJS(JSON.parse(signature_validations));
      const signedDocumentId = DRAFT_SIGNED_DOCUMENT_ID;
      // Merge (not overwrite) so per-flow metadata seeded before validate survives — e.g. DaBella's
      // custom_form_id — alongside the validation_uuid and any metadata passed as an input.
      const existingMetadata =
        (state.getIn([model, "byId", signedDocumentId, "metadata"]) as Map<string, any>) || Map<string, any>();
      const mergedMetadata = existingMetadata
        .merge({ validation_uuid: validations.getIn(["data", "id"]) })
        .merge(fromJS(metadata || {}));
      const record = signedDocumentFromJSON({
        id: signedDocumentId,
        document_id: document.id,
        estimate_id: estimateId || undefined,
        validation_status: 200,
      }).set("metadata", mergedMetadata);
      state = state.setIn([model, "byId", signedDocumentId], record);
      return reducer(state, SignedDocumentActions.initRecipients(signedDocumentId, validations));
    }

    default:
      return state;
  }
};
